<script setup lang="ts">
import type { PatternCell } from '~/utils/auth-pattern';
import { useTemplateRef } from 'vue';
import { useAuthPatternWalk } from '~/composables/use-auth-pattern-walk';
import { patternCells, VIEWBOX } from '~/utils/auth-pattern';

const pattern = useTemplateRef<SVGSVGElement>('pattern');

const {
	highlightedKey,
	isEntering,
	followPointer,
	handlePointerEnter,
	handlePointerLeave,
	handleKeydown,
	handleBlur,
	selectCell,
	handleEntryAnimationEnd,
} = useAuthPatternWalk(pattern);

const viewBox = `0 0 ${VIEWBOX.width} ${VIEWBOX.height}`;

function entryStyle(cell: PatternCell) {
	return { '--entry-delay': `${cell.entryDelay}ms` };
}
</script>

<template>
	<div
		class="
			absolute inset-0 outline-none
			focus-visible:outline-2 focus-visible:outline-offset-[-14px] focus-visible:outline-white/86
		"
		role="region"
		tabindex="0"
		:aria-label="$t('auth.login.pattern_label')"
		@pointerenter="handlePointerEnter"
		@pointermove="followPointer"
		@pointerleave="handlePointerLeave"
		@keydown="handleKeydown"
		@blur="handleBlur"
	>
		<svg
			ref="pattern"
			class="block size-full"
			:viewBox
			preserveAspectRatio="xMidYMid slice"
			aria-hidden="true"
		>
			<g
				v-for="cell in patternCells"
				:key="cell.key"
				:transform="cell.transform"
				data-pattern-cell
				:data-row="cell.row"
				:data-column="cell.column"
				:data-highlighted="cell.key === highlightedKey || undefined"
				@click="selectCell(cell)"
			>
				<g
					class="origin-center transform-fill motion-reduce:animate-none"
					:class="{
						'animate-[auth-pattern-mouth-enter_440ms_cubic-bezier(0.215,0.61,0.355,1)_var(--entry-delay)_both] will-change-[transform,opacity]': isEntering,
					}"
					:style="entryStyle(cell)"
					@animationend.self="handleEntryAnimationEnd(cell)"
				>
					<g
						class="origin-center transform-fill motion-reduce:animate-none"
						:class="{
							'animate-[auth-pattern-active-breath_2200ms_cubic-bezier(0.645,0.045,0.355,1)_infinite] will-change-transform': !isEntering && cell.key === highlightedKey,
						}"
					>
						<AuthPatternMouth />
						<AuthPatternMouth
							v-if="cell.reachable"
							active
							class="transition-opacity ease-cubic-out motion-reduce:transition-none"
							:class="cell.key === highlightedKey ? 'opacity-100 duration-120' : 'opacity-0 duration-260'"
						/>
					</g>
				</g>
			</g>
		</svg>
	</div>
</template>

<style>
@keyframes auth-pattern-active-breath {
	0%, 100% {
		transform: scale(1);
	}

	50% {
		transform: scale(0.985);
	}
}

@keyframes auth-pattern-mouth-enter {
	from {
		opacity: 0;
		transform: translateY(32px) scale(0.992);
	}

	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}
</style>
